import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import profilePhoto from './assets/profile.jpg';
import resumePDF from './assets/Samuel_Hull_Resume.pdf';

import {
  Container,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  Avatar
} from '@mui/material';
import {
  GitHub,
  LinkedIn,
  Email,
  ArrowForward,
  Code,
  School,
  Work
} from '@mui/icons-material';

function Homepage() {
  return (
    <>
      {/* Navigation Bar */}
      <AppBar position="fixed" sx={{ backgroundColor: '#A30000' }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Samuel Hull
          </Typography>
<Button color="inherit" component={RouterLink} to="/">Home</Button>
<Button color="inherit" component={RouterLink} to="/about">About</Button>
<Button color="inherit" component={RouterLink} to="/projects">Projects</Button>
<Button color="inherit" component={RouterLink} to="/contact">Contact</Button>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ mt: 10 }}>
        <Box sx={{ 
          minHeight: '60vh',
          display: 'flex', 
          alignItems: 'center',
          py: 4 
        }}>
          {/* Use Flexbox instead of Grid for hero section */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 6, 
            width: '100%',
            flexDirection: { xs: 'column', md: 'row' }
          }}>
            {/* Avatar - LEFT SIDE */}
            <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
<Avatar
  src={profilePhoto}
  alt="Sam Hull"
  sx={{
    width: { xs: 200, md: 300 },
    height: { xs: 200, md: 300 },
    backgroundColor: '#A30000'
  }}
/>
            </Box>

            {/* Content - RIGHT SIDE */}
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h2" 
                component="h1" 
                gutterBottom
                sx={{ fontWeight: 'bold', color: '#A30000' }}
              >
                Hi, I'm Sam!
              </Typography>
              <Typography 
                variant="h4" 
                component="h2" 
                gutterBottom
                sx={{ color: '#666', fontWeight: 300 }}
              >
                Master's in Computer Science Graduate from Northeastern University & Full-Stack Specialist
              </Typography>
<Typography 
  variant="h6" 
  component="p" 
  sx={{ mb: 1.5, color: '#777', lineHeight: 1.6 }}
>
  I'm passionate about AI, mobile development, and building technology that solves real problems.
  Recent Master's in Computer Science graduate from Northeastern University with hands-on experience in full-stack web and Android development, LLM integration, and machine learning.
</Typography>

<Typography 
  variant="body1" 
  component="p" 
  sx={{ mb: 3, color: '#666', lineHeight: 1.7 }}
>
With a background in Environmental Science, I bring a unique perspective to technology — I understand how data, systems, and policy intersect in the real world. My projects span a wide range: Climate Quest, a solo full-stack Android app combining Firebase, MVVM architecture, and the Anthropic Claude API to gamify sustainable living; Tarnished Tactics, a web app built with React, Node.js, MongoDB, and OpenAI's API; a machine learning pipeline for student success prediction using ensemble methods; and systems projects including a Unix-like filesystem and a multi-threaded networked database server. I'm especially motivated by AI-driven and climate-conscious technology that serves real users.
</Typography>
              
              <Box sx={{ mb: 3 }}>
                {['Python', 'Java', 'Kotlin', 'JavaScript', 'C', 'React'].map((label) => (
                  <Chip key={label} label={label} variant="outlined" sx={{ mr: 1, mb: 1 }} />
                ))}
                <Box sx={{ width: '100%' }} />
                {['Node.js', 'Android', 'Firebase', 'AI/ML', 'SQL', 'MongoDB'].map((label) => (
                  <Chip key={label} label={label} variant="outlined" sx={{ mr: 1, mb: 1 }} />
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
<Button 
  variant="contained" 
  size="large"
  endIcon={<ArrowForward />}
  component={RouterLink}
  to="/projects"
  sx={{ px: 4, backgroundColor: '#A30000', '&:hover': { backgroundColor: '#8B0000' } }}
>
  View My Work
</Button>
<Button 
  variant="outlined" 
  size="large"
  component="a"
  href={resumePDF}
  download="Samuel_Hull_Resume.pdf"
  sx={{ 
    px: 4, 
    color: '#A30000', 
    borderColor: '#A30000',
    '&:hover': { 
      backgroundColor: '#A30000', 
      color: 'white',
      borderColor: '#A30000'
    }
  }}
>
  Download Resume
</Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
<IconButton 
  size="large"
  component={RouterLink}
  to="/contact"
  sx={{ 
    border: '1px solid #A30000',
    color: '#A30000',
    '&:hover': { backgroundColor: '#A30000', color: 'white' }
  }}
>
  <Email />
</IconButton>
<IconButton 
  size="large"
  component="a"
  href="https://linkedin.com/in/samuelehull"
  target="_blank"
  rel="noopener noreferrer"
  sx={{ 
    border: '1px solid #A30000',
    color: '#A30000',
    '&:hover': { backgroundColor: '#A30000', color: 'white' }
  }}
>
  <LinkedIn />
</IconButton>
                <IconButton 
                  size="large"
                  component="a"
  href="https://github.com/samhull99"
  target="_blank"
  rel="noopener noreferrer"
                  sx={{ 
                    border: '1px solid #A30000',
                    color: '#A30000',
                    '&:hover': { backgroundColor: '#A30000', color: 'white' }
                  }}
                >
                  <GitHub />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* What I Do Section - FORCE HORIZONTAL WITH FLEXBOX */}
        <Box sx={{ pt: 0, pb: 8 }}>
          <Typography 
            variant="h4" 
            component="h2" 
            textAlign="center" 
            gutterBottom
            sx={{ mb: 6, fontWeight: 'bold' }}
          >
            What I Do
          </Typography>
          
          {/* USE FLEXBOX INSTEAD OF GRID */}
          <Box sx={{ 
            display: 'flex', 
            gap: 2,
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'center'
          }}>
            {/* Card 1 */}
            <Card 
              sx={{ 
                flex: 1,
                maxWidth: 350,
                minHeight: 280,
                textAlign: 'center',
                transition: 'transform 0.2s',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                }
              }}
            >
              <CardContent sx={{ 
                p: 4, 
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                flex: 1
              }}>
<Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
  <Code sx={{ fontSize: 48, color: '#A30000' }} />
</Box>
                <Typography variant="h5" component="h3" gutterBottom>
                  Full-Stack & Mobile Development
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Building web and Android applications with React, Node.js, MongoDB, Java, Firebase, and MVVM architecture. Focused on responsive, user-centered design.
                </Typography>
              </CardContent>
            </Card>

            {/* Card 2 */}
            <Card 
              sx={{ 
                flex: 1,
                maxWidth: 350,
                minHeight: 280,
                textAlign: 'center',
                transition: 'transform 0.2s',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                }
              }}
            >
              <CardContent sx={{ 
                p: 4, 
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                flex: 1
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
  <School sx={{ fontSize: 48, color: '#A30000' }} />
</Box>
                <Typography variant="h5" component="h3" gutterBottom>
                  AI & Machine Learning
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Integrating LLMs like Claude and GPT into production apps, and building ML pipelines using ensemble methods, meta-learning, and feature analysis.
                </Typography>
              </CardContent>
            </Card>

            {/* Card 3 */}
            <Card 
              sx={{ 
                flex: 1,
                maxWidth: 350,
                minHeight: 280,
                textAlign: 'center',
                transition: 'transform 0.2s',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': { 
                  transform: 'translateY(-4px)',
                  boxShadow: 3
                }
              }}
            >
              <CardContent sx={{ 
                p: 4, 
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                flex: 1
              }}>
<Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
  <Work sx={{ fontSize: 48, color: '#A30000' }} />
</Box>
                <Typography variant="h5" component="h3" gutterBottom>
                  Systems Programming
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Low-level experience in C including a FUSE-based read-write filesystem and a multi-threaded TCP database server with concurrent worker thread architecture.
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Container>
    </>
  );
}

export default Homepage;